import { useQuery } from '@tanstack/react-query';
import type { ReportSummary } from 'fuel-benchmark-contracts';
import { Plus } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { InfoCell } from '../../components/ui/InfoCell';
import { formatDay } from '../../lib/format';
import { serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { REPORT_BUILDER_PATH } from './report-display';

/** Reports are few and read chronologically; one page of 100 keeps client-side sorting honest. */
const PAGE_SIZE = 100;

/**
 * Saved reports (plan §7 `/reports`). Every row is a frozen document: the title, scenario and vehicle labels
 * come out of the snapshot as it was published, so the list keeps reading as history even after a vehicle is
 * renamed or a scenario retired. A row click opens the print-styled renderer.
 */
export const ReportsListPage = () => {
  const { t } = useI18nTranslation();
  const navigate = useNavigate();

  const reports = useQuery(orpc.reports.list.queryOptions({ input: { page: 1, pageSize: PAGE_SIZE, sortBy: 'createdAt', sortDirection: 'desc' } }));

  const columns: DataTableColumns<ReportSummary> = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: t('reports.columns.title'),
        cell: ({ row }) => (
          <InfoCell
            title={row.original.title}
            subline={
              row.original.runCount === 1 ? t('reports.columns.runCountOne') : t('reports.columns.runCountMany', { count: row.original.runCount })
            }
          />
        ),
      },
      {
        accessorKey: 'scenarioCode',
        header: t('reports.columns.tests'),
        // A report spans one to six tests; the code names the first of them, the line beneath it says how many.
        cell: ({ row }) => (
          <InfoCell
            title={row.original.scenarioCode}
            subline={
              row.original.testCount === 1 ? t('reports.columns.testCountOne') : t('reports.columns.testCountMany', { count: row.original.testCount })
            }
          />
        ),
      },
      {
        id: 'vehicles',
        accessorFn: (row) => row.vehicleLabels.join(', '),
        header: t('reports.columns.vehicles'),
        cell: ({ row }) => <span className="text-sm">{row.original.vehicleLabels.join(' · ')}</span>,
      },
      {
        accessorKey: 'createdByName',
        header: t('reports.columns.author'),
        cell: ({ row }) => <span className="text-sm">{row.original.createdByName ?? t('common.notSet')}</span>,
      },
      {
        accessorKey: 'createdAt',
        header: t('reports.columns.created'),
        cell: ({ row }) => <span className="whitespace-nowrap text-sm">{formatDay(row.original.createdAt)}</span>,
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('reports.title')}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t('reports.subtitle')}</p>
        </div>
        {/* Straight to the builder: it owns the vehicle step and the test list, so a report never has to
            start on the compare screen (the compare hand-off still lands on the same page pre-filled). */}
        <Button onClick={() => navigate(REPORT_BUILDER_PATH)}>
          <Plus className="size-4" />
          {t('reports.newReport')}
        </Button>
      </div>

      {reports.isError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {serverMessage(reports.error) ?? t('errors.loadFailed')}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        data={reports.data?.items ?? []}
        isLoading={reports.isPending}
        emptyMessage={t('reports.empty')}
        onRowClick={(report) => navigate(`/reports/${report.id}`)}
      />
    </div>
  );
};
